import { Capacitor } from "@capacitor/core";
import { PowerSyncDatabase as PowerSyncDatabaseCapacitor } from "@powersync/capacitor";
import {
  type CommonPowerSyncDatabase,
  type CrudEntry,
  LogLevels,
  type LogRecord,
  type PowerSyncBackendConnector,
  PowerSyncDatabase as PowerSyncDatabaseWeb,
  WASQLiteVFS,
} from "@powersync/web";
import * as Sentry from "@sentry/capacitor";
import type { PowerSyncUploadResult } from "@vidimo/shared/contracts";
import { AppSchema } from "@/lib/powersync-schema.generated";
import { uploadDataFor } from "@/lib/rates/night-upload-key";
import { isFirstReport } from "@/lib/report-distinct";
import { SUPPORT_SESSION } from "@/lib/support-session";
import { ApiRequestError, NETWORK_UNREACHABLE } from "./api/api-error";
import { getPowerSyncToken } from "./api/powersync";
import { getApiUrl } from "./api-url";
import { reportDiscardedUploadOps } from "./powersync-upload-feedback";

console.log(
  "[powersync] platform:",
  Capacitor.getPlatform(),
  "isNative:",
  Capacitor.isNativePlatform(),
  "PowerSync plugin available:",
  Capacitor.isPluginAvailable("PowerSync"),
  "CapacitorSQLite available:",
  Capacitor.isPluginAvailable("CapacitorSQLite")
);

/**
 * PowerSync local-first SQLite. Native (iOS/Android): @powersync/capacitor
 * (CapacitorSQLite). PWA/web: Web SDK (wa-sqlite). wa-sqlite under iOS WKWebView
 * crashes the WebContent process (memory limits + OPFS/SAB constraints).
 */
const PowerSyncDatabaseImpl: typeof PowerSyncDatabaseWeb =
  Capacitor.isNativePlatform()
    ? PowerSyncDatabaseCapacitor
    : PowerSyncDatabaseWeb;

const DB_FILENAME = "superhost.db";

/**
 * A support tab's replica lives in memory, in a worker of its own: it is gone
 * when the tab closes and never meets the agent's own replica in the same
 * browser (lib/support-session.ts). Its name is its own too, since PowerSync
 * takes the browser's locks by database name: under the app's, the tab waits
 * on the agent's own app syncing beside it and never syncs.
 */
function supportTabDatabase() {
  return {
    database: {
      dbFilename: `support-${crypto.randomUUID()}.db`,
      enableMultiTabs: false,
      vfs: WASQLiteVFS.InMemoryVfs,
    },
  };
}

const TOKEN_FAILURE_PREFIX = "[powersync] token failed";

/** Records the collections report themselves (collections/_factory.ts). */
const UNREPORTED_RECORD_PREFIXES = [
  "Failed to validate incoming data",
  "Unknown deserialization error for",
];

/**
 * The sync service's answer, read from the record's text: on the web the
 * record crosses from the sync worker as a structured clone, which keeps an
 * error's name and message and drops its `status`.
 */
const STREAM_REFUSAL =
  /^Could not POST streaming to \S+ - (?<status>\d{3}) - /u;

/**
 * A stale cached token, which the SDK drops and refetches on its own retry. A
 * token that cannot be refetched reports as its own "token failed" error.
 */
const UNAUTHORIZED = "401";

/** The adapter names its tracking table with a random suffix per start. */
const TRACKING_TABLE = / into \S+$/u;

/**
 * A "Sync error" the connection banner covers and every retry repeats: the
 * device lost its network or cannot reach the API for a token, or the service
 * refused the stream, which its "Could not POST streaming" record has already
 * reported with the status.
 */
function isRoutineSyncError(error: unknown): boolean {
  return (
    !navigator.onLine ||
    (error instanceof Error &&
      (["AbortError", "TypeError"].includes(error.name) ||
        error.message.startsWith("HTTP ") ||
        error.message === `${TOKEN_FAILURE_PREFIX} ${NETWORK_UNREACHABLE}`))
  );
}

const reportedRecords = new Set<string>();

/**
 * Error-level SDK records reach Sentry, each distinct one once: the record's
 * own message and the error's name, never the error's message, which can carry
 * a row key, and some row ids are secrets (`rowIdIsSecret`). Nothing reaches
 * the console: every console line is a Sentry breadcrumb.
 */
function reportErrorRecord({ error, level, message }: LogRecord) {
  if (
    level < LogLevels.error ||
    UNREPORTED_RECORD_PREFIXES.some((prefix) => message.startsWith(prefix)) ||
    (message === "Sync error" && isRoutineSyncError(error))
  ) {
    return;
  }
  const refusal = STREAM_REFUSAL.exec(message)?.groups?.status;
  if (refusal === UNAUTHORIZED) {
    return;
  }
  const text = message.replace(TRACKING_TABLE, "");
  let summary = text;
  if (refusal) {
    summary = `sync stream refused ${refusal}`;
  } else if (error instanceof Error) {
    const ownWording = error.message.startsWith(TOKEN_FAILURE_PREFIX);
    summary = `${text} (${ownWording ? error.message : error.name})`;
  }
  if (isFirstReport(reportedRecords, summary)) {
    Sentry.captureException(new Error(`PowerSync: ${summary}`), {
      fingerprint: ["powersync-sdk", summary],
      tags: { area: "powersync-sdk" },
    });
  }
}

export const powerSyncDb = new PowerSyncDatabaseImpl({
  ...(SUPPORT_SESSION
    ? supportTabDatabase()
    : { database: { dbFilename: DB_FILENAME } }),
  logger: { log: reportErrorRecord },
  schema: AppSchema,
});

function resolvePowerSyncEndpoint(endpoint: string) {
  if (!endpoint.trim()) {
    throw new Error("powersync endpoint is empty");
  }
  // Local web dev: point `powersync.url` / token URL at `/powersync` (Vite proxy → :8080).
  // Native: use absolute URL from backend (e.g. https://powersync.example.com).
  return endpoint;
}

/**
 * The status goes in the message: a token failure reaches the logger and the
 * stuck-queue report through the sync worker, which keeps only an error's
 * name and message.
 */
async function powerSyncToken() {
  try {
    return await getPowerSyncToken();
  } catch (err) {
    const status = err instanceof ApiRequestError ? err.status : "unknown";
    throw new Error(`${TOKEN_FAILURE_PREFIX} ${status}`, { cause: err });
  }
}

class VidimoBackendConnector implements PowerSyncBackendConnector {
  async fetchCredentials() {
    const { powersyncUrl, token } = await powerSyncToken();
    return { endpoint: resolvePowerSyncEndpoint(powersyncUrl), token };
  }

  async uploadData(database: CommonPowerSyncDatabase) {
    const uploadUrl = getApiUrl("/powersync/upload");
    const { token } = await powerSyncToken();

    let transaction = await database.getNextCrudTransaction();
    while (transaction) {
      // biome-ignore lint/performance/noAwaitInLoops: PowerSync CRUD transactions must upload in order — each transaction.complete() must land before the next getNextCrudTransaction() is fetched, so batches can't run concurrently.
      const ops = await Promise.all(
        transaction.crud.map(async (entry: CrudEntry) => {
          // CrudEntry uses `table` + `opData` (see @powersync/common).
          const { table } = entry;
          if (!table) {
            throw new Error(
              "[powersync] CRUD entry missing table (expected CrudEntry.table)"
            );
          }
          return {
            data: await uploadDataFor(entry, database),
            id: entry.id,
            op: entry.op,
            type: table,
          };
        })
      );

      const response = await fetch(uploadUrl, {
        body: JSON.stringify({
          bucket: "global",
          data: ops,
        }),
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        method: "POST",
      });

      if (!response.ok) {
        const { status } = response;
        const body = await response.text().catch(() => "");
        throw new Error(
          `[powersync] upload failed ${status}${
            body ? `: ${body.slice(0, 200)}` : ""
          }`
        );
      }

      let payload: { completed?: PowerSyncUploadResult[] } = {};
      try {
        payload = (await response.json()) as typeof payload;
      } catch {
        /* non-JSON success — still complete if server applied batch */
      }
      // Discarding a rejected op keeps the queue moving, but it must be visible:
      // surface it to the user (toast) and to us (Sentry). Pass op identities
      // only, never the row payload (guest PII).
      const failed = payload.completed?.filter((c) => c.error) ?? [];
      if (failed.length > 0) {
        reportDiscardedUploadOps(
          failed,
          ops.map(({ op, type, id }) => ({ id, op, type }))
        );
      }

      await transaction.complete();
      transaction = await database.getNextCrudTransaction();
    }
  }
}

const connector = new VidimoBackendConnector();

export async function connectPowerSync() {
  // No options on purpose. @powersync/capacitor defaults the native driver to
  // HTTP and reports `supportsStreamingBinaryResponses: false`, which the
  // shared-internals patch turns into an NDJSON-only request, so sync lines
  // arrive as strings and no Uint8Array is ever bound as a SQL parameter.
  // Passing `connectionMethod: WEB_SOCKET` re-opens the binary path, and
  // upstream measures that at 68s against 449ms for 10k records on iOS.
  // Re-check both gates when @powersync/capacitor or @powersync/web moves.
  await powerSyncDb.connect(connector);
}

export async function disconnectPowerSync() {
  await powerSyncDb.disconnect();
}
